import type { MessageIntent } from '@/modules/template';
import {
  ShareWizardLogic,
  type ContactsByIntent,
  type ShareWizardStep,
} from './logic';
import type {
  ShareDestination,
  ShareFlowShape,
  ShareRouteTable,
  ShareSurface,
} from './types';

/**
 * The destinations a sharing session can ask for, as values. Naming them here
 * rather than spelling the object out at each call site means a surface asks
 * for `Destinations.style` and never for the route that happens to show it.
 */
export const Destinations: {
  readonly recipients: ShareDestination;
  readonly changes: ShareDestination;
  readonly style: ShareDestination;
  readonly composer: ShareDestination;
  readonly send: ShareDestination;
  readonly sent: ShareDestination;
  readonly message: (intent: MessageIntent) => ShareDestination;
} = {
  recipients: { name: 'recipients' },
  changes: { name: 'changes' },
  style: { name: 'style' },
  composer: { name: 'composer' },
  send: { name: 'send' },
  sent: { name: 'sent' },
  message: (intent: MessageIntent) => ({ name: 'message', intent }),
};

/** The destination names the wizard renders. */
type WizardDestinationName = Exclude<ShareDestination['name'], 'composer'>;

/** The destination names the garden flow renders. */
type GardenDestinationName = Exclude<
  ShareDestination['name'],
  'changes' | 'style' | 'message'
>;

/** The URL segments under `/share/steps/` the garden flow uses. */
type GardenSegment =
  | 'recipients'
  | 'composer'
  | 'share-confirm'
  | 'final-share-confirm';

/**
 * The wizard's step for each destination, and back again. A new step or a new
 * destination has to be given its counterpart here or the record fails to
 * compile, so the two vocabularies cannot drift apart.
 */
const STEP_FOR_NAME: Record<
  Exclude<WizardDestinationName, 'message'>,
  ShareWizardStep
> = {
  recipients: 'recipients',
  changes: 'changes',
  style: 'tone',
  send: 'share-confirm',
  sent: 'final-share-confirm',
};

const DESTINATION_FOR_STEP: Record<ShareWizardStep, ShareDestination> = {
  recipients: Destinations.recipients,
  changes: Destinations.changes,
  tone: Destinations.style,
  'intro-message': Destinations.message('intro'),
  'reminder-message': Destinations.message('reminder'),
  'update-message': Destinations.message('update'),
  'share-confirm': Destinations.send,
  'final-share-confirm': Destinations.sent,
};

/**
 * The garden flow's segment for each of its destinations, and back again. The
 * recipient destination keeps the wizard's segment because it is the same
 * place, and the two sending destinations keep theirs because they are the
 * very same screens: the garden flow hands off to the send loop unchanged.
 */
const GARDEN_SEGMENT_FOR_NAME: Record<GardenDestinationName, GardenSegment> = {
  recipients: 'recipients',
  composer: 'composer',
  send: 'share-confirm',
  sent: 'final-share-confirm',
};

const GARDEN_DESTINATION_FOR_SEGMENT: Record<GardenSegment, ShareDestination> =
  {
    recipients: Destinations.recipients,
    composer: Destinations.composer,
    'share-confirm': Destinations.send,
    'final-share-confirm': Destinations.sent,
  };

/**
 * The garden destination standing in for each wizard-only one. Everything
 * between picking people and sending is one surface in the garden flow: the
 * change summary the Changes step collects sits at the top of the composer's
 * Form tab, the message steps' three messages are answered there in one pass,
 * and there is no tone to pick at all.
 */
const GARDEN_NEAREST: Record<
  Exclude<ShareDestination['name'], GardenDestinationName>,
  ShareDestination
> = {
  changes: Destinations.composer,
  style: Destinations.composer,
  message: Destinations.composer,
};

/**
 * The wizard destination standing in for each garden-only one. A sender in the
 * composer has done work the wizard has no one screen for, so they land on the
 * recipient destination — the wizard's own way out of a state it cannot
 * render, and where `goBack` already falls back to.
 */
const WIZARD_NEAREST: Record<
  Exclude<ShareDestination['name'], WizardDestinationName>,
  ShareDestination
> = {
  composer: Destinations.recipients,
};

const STEP_PATTERN = /\/share\/steps\/([a-z-]+)(?:\/|$)/;

function indexOfDestination(
  sequence: readonly ShareDestination[],
  destination: ShareDestination
): number {
  return sequence.findIndex(candidate =>
    ShareNavigationLogic.same(candidate, destination)
  );
}

/** The segment of a `/share/steps/<segment>` path, or null for any other URL. */
function segmentOf(path: string): string | null {
  const match = path.match(STEP_PATTERN);
  return match ? match[1] : null;
}

function sharePath(topicId: string, segment: string, search: string): string {
  const base = `/topics/${encodeURIComponent(topicId)}/share/steps/${segment}`;
  const query = search.replace(/^\?/, '');
  return query ? `${base}?${query}` : base;
}

function wizardSegment(destination: ShareDestination): ShareWizardStep {
  switch (destination.name) {
    case 'message':
      return ShareWizardLogic.stepForIntent(destination.intent);
    case 'composer':
      return wizardSegment(WIZARD_NEAREST[destination.name]);
    default:
      return STEP_FOR_NAME[destination.name];
  }
}

function gardenSegment(destination: ShareDestination): GardenSegment {
  switch (destination.name) {
    case 'changes':
    case 'style':
    case 'message':
      return gardenSegment(GARDEN_NEAREST[destination.name]);
    default:
      return GARDEN_SEGMENT_FOR_NAME[destination.name];
  }
}

const WizardRoutes: ShareRouteTable = {
  surface: 'wizard',

  sequence(flow: ShareFlowShape): readonly ShareDestination[] {
    return ShareWizardLogic.visibleSteps({
      contactsByIntent: flow.contactsByIntent,
      sharingMode: flow.sharingMode,
      isUpdateFlow: flow.isUpdateFlow,
      sendQueueLength: flow.sendQueueLength,
    }).map(step => DESTINATION_FOR_STEP[step]);
  },

  pathFor(topicId: string, destination: ShareDestination, search = ''): string {
    return sharePath(topicId, wizardSegment(destination), search);
  },

  destinationFromPath(path: string): ShareDestination {
    const segment = segmentOf(path);
    return segment && ShareWizardLogic.isStep(segment)
      ? DESTINATION_FOR_STEP[segment]
      : Destinations.recipients;
  },

  nearest(destination: ShareDestination): ShareDestination {
    return destination.name === 'composer'
      ? WIZARD_NEAREST[destination.name]
      : destination;
  },

  rendersPath(path: string): boolean {
    const segment = segmentOf(path);
    return segment === null || ShareWizardLogic.isStep(segment);
  },
};

const GardenRoutes: ShareRouteTable = {
  surface: 'garden',

  /**
   * Two destinations, then the send loop for a restored session that already
   * holds a send queue; a new share goes to the outbox instead. The composer
   * is always shown:
   * unlike the wizard's per-intent message steps, one composer answers for
   * every intent, so there is no intent that can empty it out.
   */
  sequence(flow: ShareFlowShape): readonly ShareDestination[] {
    const base: readonly ShareDestination[] = [
      Destinations.recipients,
      Destinations.composer,
    ];
    if (flow.sendQueueLength === 0) return base;
    const legs: ShareDestination[] = Array(flow.sendQueueLength).fill(
      Destinations.send
    );
    return [...base, ...legs, Destinations.sent];
  },

  pathFor(topicId: string, destination: ShareDestination, search = ''): string {
    return sharePath(topicId, gardenSegment(destination), search);
  },

  destinationFromPath(path: string): ShareDestination {
    const segment = segmentOf(path);
    return segment && isGardenSegment(segment)
      ? GARDEN_DESTINATION_FOR_SEGMENT[segment]
      : Destinations.recipients;
  },

  nearest(destination: ShareDestination): ShareDestination {
    return destination.name === 'changes' ||
      destination.name === 'style' ||
      destination.name === 'message'
      ? GARDEN_NEAREST[destination.name]
      : destination;
  },

  rendersPath(path: string): boolean {
    const segment = segmentOf(path);
    return segment === null || isGardenSegment(segment);
  },
};

function isGardenSegment(value: string): value is GardenSegment {
  return Object.prototype.hasOwnProperty.call(
    GARDEN_DESTINATION_FOR_SEGMENT,
    value
  );
}

/**
 * The route table for each surface. A surface added here without a table fails
 * to compile, which is what keeps `routesFor` total.
 */
export const ShareRoutes = {
  wizard: WizardRoutes,
  garden: GardenRoutes,
} as const satisfies Record<ShareSurface, ShareRouteTable>;

/** How `surface` routes a sharing session. */
export function routesFor(surface: ShareSurface): ShareRouteTable {
  return ShareRoutes[surface];
}

/**
 * Whether any surface would send `path` somewhere else — true when at least
 * one of them has no destination for it.
 *
 * `ShareFlowGate` asks this before the app config has arrived, so it must be
 * answerable without knowing which surface is in charge. A path both surfaces
 * have a destination for goes on screen straight away, whichever one wins; a
 * path only one of them has to offer waits for the flag first.
 */
export function anySurfaceResumes(path: string): boolean {
  return !WizardRoutes.rendersPath(path) || !GardenRoutes.rendersPath(path);
}

/**
 * Where a URL this surface cannot render resumes on it, or null when the surface
 * renders the URL already.
 *
 * A session started under one flag setting has to resume under the other, and
 * a link written when the wizard was the flow has to survive the garden flow
 * taking over. Both come down to one question the URL puts to whichever
 * surface is now in charge: is this one of mine, and if not, which of mine
 * stands for it.
 *
 * The answer belongs above the flow's `IonPage`, not in a fallback route
 * inside its outlet. Redirecting is a history *replace*, and StackManager
 * unmounts the leaving view on a replace without first checking that it is not
 * also the entering one. The flow's route is not exact, so every step URL
 * under it is the same view: replacing one step URL with another takes the
 * page down and everything the outlet was rendering with it. Resolving this in
 * `ShareFlowGate`, before the shell mounts, means the replace happens while
 * that view has no `IonPage` registered — which is the one state StackManager
 * does not treat as a view to unmount.
 */
export function resumePathOnSurface(
  surface: ShareSurface,
  topicId: string,
  path: string,
  search: string
): string | null {
  const here = ShareRoutes[surface];
  if (here.rendersPath(path)) return null;
  const other = ShareRoutes[surface === 'garden' ? 'wizard' : 'garden'];
  return here.pathFor(
    topicId,
    here.nearest(other.destinationFromPath(path)),
    search
  );
}

export const ShareNavigationLogic = {
  /** Whether two destinations name the same place. */
  same(a: ShareDestination, b: ShareDestination): boolean {
    if (a.name !== b.name) return false;
    if (a.name === 'message' && b.name === 'message') {
      return a.intent === b.intent;
    }
    return true;
  },

  /** The wizard's step for a destination, and back again. */
  stepFor(destination: ShareDestination): ShareWizardStep {
    return wizardSegment(destination);
  },

  destinationForStep(step: ShareWizardStep): ShareDestination {
    return DESTINATION_FOR_STEP[step];
  },

  /**
   * Where `destination` sits in the sequence, 1-based, or null when it is not
   * in it. Every queued leg shares the one send destination, so the cursor
   * picks which of them the sender is on.
   */
  positionIn(
    sequence: readonly ShareDestination[],
    destination: ShareDestination,
    sendQueueCursor: number
  ): number | null {
    if (destination.name === 'send') {
      const first = sequence.findIndex(entry => entry.name === 'send');
      return first === -1 ? null : first + sendQueueCursor + 1;
    }
    const idx = indexOfDestination(sequence, destination);
    return idx === -1 ? null : idx + 1;
  },

  /**
   * How far along the sequence `destination` is, from 0 to 1. A destination
   * that has dropped out of the sequence reads as past its end, which fills
   * the bar rather than emptying it.
   */
  progressAt(
    sequence: readonly ShareDestination[],
    destination: ShareDestination,
    sendQueueCursor: number
  ): number {
    const position =
      ShareNavigationLogic.positionIn(sequence, destination, sendQueueCursor) ??
      sequence.length + 1;
    return position / (sequence.length + 1);
  },

  /** The destination after `destination`, or null at the end of the sequence. */
  after(
    sequence: readonly ShareDestination[],
    destination: ShareDestination
  ): ShareDestination | null {
    const idx = indexOfDestination(sequence, destination);
    return idx >= 0 && idx < sequence.length - 1 ? sequence[idx + 1] : null;
  },

  /** The destination before `destination`, or null at the start of the sequence. */
  before(
    sequence: readonly ShareDestination[],
    destination: ShareDestination
  ): ShareDestination | null {
    const idx = indexOfDestination(sequence, destination);
    return idx > 0 ? sequence[idx - 1] : null;
  },

  /** The first message destination with anyone to text, if there is one. */
  firstCompose(contactsByIntent: ContactsByIntent): ShareDestination | null {
    const step =
      ShareWizardLogic.firstComposeStepWithContacts(contactsByIntent);
    return step ? DESTINATION_FOR_STEP[step] : null;
  },

  /**
   * The message destination after `intent`'s with anyone to text, skipping
   * the empty ones. Null when `intent`'s is the last one with recipients.
   */
  nextCompose(
    contactsByIntent: ContactsByIntent,
    intent: MessageIntent
  ): ShareDestination | null {
    const step = ShareWizardLogic.nextComposeStepAfter(
      contactsByIntent,
      ShareWizardLogic.stepForIntent(intent)
    );
    return step ? DESTINATION_FOR_STEP[step] : null;
  },

  /** Whether `destination` is the last message destination in the sequence. */
  isLastMessage(
    sequence: readonly ShareDestination[],
    destination: ShareDestination
  ): boolean {
    if (destination.name !== 'message') return false;
    const messageDestinations = sequence.filter(
      entry => entry.name === 'message'
    );
    const last = messageDestinations[messageDestinations.length - 1];
    return last != null && ShareNavigationLogic.same(last, destination);
  },

  /**
   * Whether `destination` is one of the two sending destinations. They share a
   * header layout (no progress bar, an X in the end slot) and bypass the
   * empty-recipients snap guard.
   */
  isSending(destination: ShareDestination): boolean {
    return destination.name === 'send' || destination.name === 'sent';
  },
};
